/**
 * VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
 * FOUNDATION_P0_V1 — "积分奖励规则" tab (task spec §8/§9).
 *
 * This round is Admin Rule Foundation ONLY — task spec §9's
 * `TENANT_DAILY_REWARD_CLAIM_API=NOT_IMPLEMENTED`: no tenant-facing "积分
 * 加油站" claim button/endpoint exists yet, so this tab's own banner says
 * so explicitly rather than implying a working end-to-end feature.
 */
import { Alert, Button, DatePicker, Drawer, Form, InputNumber, Select, Switch, Table, message } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import {
  platformCreateDailyRewardRule,
  platformDeleteDailyRewardRule,
  platformListBillingPlans,
  platformListDailyRewardRules,
  platformUpdateDailyRewardRule,
} from '../../api'
import type { BillingPlan, DailyRewardRule } from '../../types'
import { SectionCard } from '../adminShared'
import { errMsg, formatCredits, formatDateTimeShort } from './billingShared'

const CLAIM_METHOD_LABEL: Record<string, string> = {
  CLAIM_REQUIRED: '需用户主动领取（积分加油站）',
  AUTO_GRANT: '自动到账（登录即到账）',
}

export default function DailyRewardTab() {
  const [rules, setRules] = useState<DailyRewardRule[]>([])
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<DailyRewardRule | null>(null)

  const load = () => {
    setLoading(true)
    setError(null)
    Promise.all([platformListDailyRewardRules(), platformListBillingPlans()])
      .then(([r, p]) => {
        setRules(r.data)
        setPlans(p.data)
      })
      .catch((err) => setError(errMsg(err, '加载每日奖励规则失败')))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const toggle = async (row: DailyRewardRule, enabled: boolean) => {
    try {
      const res = await platformUpdateDailyRewardRule(row.id, { enabled })
      setRules((prev) => prev.map((r) => (r.id === row.id ? res.data : r)))
    } catch (err) {
      message.error(errMsg(err, '更新失败'))
    }
  }

  const remove = async (row: DailyRewardRule) => {
    try {
      await platformDeleteDailyRewardRule(row.id)
      setRules((prev) => prev.filter((r) => r.id !== row.id))
      message.success('已删除')
    } catch (err) {
      message.error(errMsg(err, '删除失败'))
    }
  }

  const planLabel = (keys: string[] | null) => {
    if (!keys || keys.length === 0) return '全部套餐'
    return keys.map((k) => plans.find((p) => p.key === k)?.display_name ?? k).join(' / ')
  }

  return (
    <div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="本轮为 Admin 配置基础（Foundation）"
        description="租户前端「积分加油站」主动领取按钮与领取 API 本轮尚未实现（TENANT_DAILY_REWARD_CLAIM_API=NOT_IMPLEMENTED）。这里配置的规则会在该功能上线后生效；默认领取方式为 CLAIM_REQUIRED（登录 ≠ 自动到账，需用户主动点击领取），并由后端防止同一自然日重复领取。"
      />
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}

      <SectionCard
        title="每日积分加油站规则（Daily Reward Rule）"
        extra={
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setEditing(null)
              setOpen(true)
            }}
          >
            + 新增规则
          </Button>
        }
      >
        <Table
          className="pf-data-table"
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={rules}
          pagination={false}
          columns={[
            { title: '每日 Credits', width: 110, render: (_: unknown, row: DailyRewardRule) => formatCredits(row.daily_credits) },
            { title: '领取方式', dataIndex: 'claim_method', width: 220, render: (v: string) => CLAIM_METHOD_LABEL[v] ?? v },
            { title: '每日最多次数', dataIndex: 'max_claims_per_day', width: 110 },
            { title: '开始时间', dataIndex: 'start_at', width: 160, render: (v: string | null) => formatDateTimeShort(v) },
            { title: '结束时间', dataIndex: 'end_at', width: 160, render: (v: string | null) => formatDateTimeShort(v) },
            { title: '适用套餐', dataIndex: 'applicable_plan_keys', render: (v: string[] | null) => planLabel(v) },
            {
              title: '启用',
              dataIndex: 'enabled',
              width: 80,
              render: (v: boolean, row) => <Switch size="small" checked={v} onChange={(checked) => toggle(row, checked)} />,
            },
            {
              title: '操作',
              width: 130,
              render: (_: unknown, row: DailyRewardRule) => (
                <>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      setEditing(row)
                      setOpen(true)
                    }}
                  >
                    编辑
                  </Button>
                  <Button size="small" type="link" danger onClick={() => void remove(row)}>
                    删除
                  </Button>
                </>
              ),
            },
          ]}
        />
      </SectionCard>

      <DailyRewardDrawer
        open={open}
        editing={editing}
        plans={plans}
        onClose={() => setOpen(false)}
        onCreated={(r) => setRules((prev) => [...prev, r])}
        onUpdated={(r) => setRules((prev) => prev.map((x) => (x.id === r.id ? r : x)))}
      />
    </div>
  )
}

function DailyRewardDrawer({
  open,
  editing,
  plans,
  onClose,
  onCreated,
  onUpdated,
}: {
  open: boolean
  editing: DailyRewardRule | null
  plans: BillingPlan[]
  onClose: () => void
  onCreated: (r: DailyRewardRule) => void
  onUpdated: (r: DailyRewardRule) => void
}) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing) {
      form.setFieldsValue({
        enabled: editing.enabled,
        daily_credits: Number(editing.daily_credits),
        claim_method: editing.claim_method,
        max_claims_per_day: editing.max_claims_per_day,
        date_range: editing.start_at && editing.end_at ? [dayjs(editing.start_at), dayjs(editing.end_at)] : undefined,
        applicable_plan_keys: editing.applicable_plan_keys ?? undefined,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ enabled: false, daily_credits: 0, claim_method: 'CLAIM_REQUIRED', max_claims_per_day: 1 })
    }
  }, [open, editing, form])

  const submit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      const payload = {
        enabled: values.enabled,
        daily_credits: values.daily_credits,
        claim_method: values.claim_method,
        max_claims_per_day: values.max_claims_per_day,
        start_at: values.date_range?.[0] ? values.date_range[0].toISOString() : null,
        end_at: values.date_range?.[1] ? values.date_range[1].toISOString() : null,
        applicable_plan_keys: values.applicable_plan_keys?.length ? values.applicable_plan_keys : null,
      }
      if (editing) {
        const res = await platformUpdateDailyRewardRule(editing.id, payload)
        onUpdated(res.data)
        message.success('规则已更新')
      } else {
        const res = await platformCreateDailyRewardRule(payload)
        onCreated(res.data)
        message.success('规则已创建')
      }
      onClose()
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer title={editing ? '编辑每日奖励规则' : '新增每日奖励规则'} open={open} onClose={onClose} width={440} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="enabled" label="启用" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="daily_credits" label="每日 Credits" rules={[{ required: true }]}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="claim_method" label="领取方式" rules={[{ required: true }]}>
          <Select
            options={[
              { label: 'CLAIM_REQUIRED — 需用户主动领取（积分加油站）', value: 'CLAIM_REQUIRED' },
              { label: 'AUTO_GRANT — 自动到账', value: 'AUTO_GRANT' },
            ]}
          />
        </Form.Item>
        <Form.Item name="max_claims_per_day" label="每日最多领取次数" rules={[{ required: true }]}>
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="date_range" label="活动时间范围（留空为长期有效）">
          <DatePicker.RangePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="applicable_plan_keys" label="适用套餐（留空为全部套餐）">
          <Select mode="multiple" options={plans.map((p) => ({ label: p.display_name, value: p.key }))} allowClear />
        </Form.Item>
      </Form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>取消</Button>
        <Button type="primary" loading={submitting} onClick={() => void submit()}>
          {editing ? '保存' : '创建'}
        </Button>
      </div>
    </Drawer>
  )
}
